import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN, Data } from '@blade/std-lib'
import { CircularLoader } from '@blade/ui-kit'
import { UserShort } from '@root/entities/user-short'
import { WalletAddressTruncated } from '@root/entities/wallet/ui/common/wallet-address-truncated'
import { DomUtil } from '@root/shared'
import { CommentExactIcon, LikeIcon } from '@root/shared/ui-kit'

import BG_FRAME from '../assets/invite-code-dialog.png'

import { InviteCodeUserAvatar } from './invite-code-user-avatar'

import type { UserInviteCodeFormInviterState } from '@root/features/referral/atoms/reatom-use-invite-code-form'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  inviterStateAtom: UserInviteCodeFormInviterState
} & ClassNameProps

export const InviteCodeUserInfo = ({ inviterStateAtom, className }: Props) => {
  const { t } = useTranslation()
  const [inviterState] = useAtom(inviterStateAtom)

  const counters = {
    followers: inviterState.value?.followers ?? 0,
    following: inviterState.value?.following ?? 0,
    likes: inviterState.value?.likesCount ?? 0,
    mentions: inviterState.value?.mentionsCount ?? 0,
  }

  return (
    <Root className={className}>
      <Frame>
        <Header>
          <HeaderItem>
            <span>{counters.followers}</span>
            {t('common.follower', {
              count: counters.followers,
            })}
          </HeaderItem>
          <InviteCodeUserAvatar inviterStateAtom={inviterStateAtom} />
          <HeaderItem>
            <span>{counters.following}</span>
            {t('common.following', {
              count: counters.following,
            })}
          </HeaderItem>
        </Header>
        <Body>
          {AsyncStateN.match(inviterState, {
            NotInitiated: () => (
              <>
                <Username>{t('auth.username')}</Username>
                <Description>{t('wallet_one')}</Description>
              </>
            ),
            Success: (userData) => (
              <>
                <Username>{UserShort.someName(userData)}</Username>
                {userData.wallet ? (
                  <WalletDescription walletAddress={userData.wallet} />
                ) : undefined}
              </>
            ),
            Loading: () => (
              <div>
                <CircularLoader size={14} />
              </div>
            ),
            Error: (x) => {
              const errorMessage = Data.match.I(x)({
                ApiError: () => 'error.auth.inviter-not-found' as const,
                UnknownError: () => 'common.something-went-wrong' as const,
              })
              return (
                <>
                  <Error>{t(errorMessage)}</Error>
                  <Description>{t('referral.try-another-code')}</Description>
                </>
              )
            },
          })}
        </Body>
        <Footer>
          <FooterItem>
            <LikeIcon size={10} color="var(--textA-8)" />
            <span>{counters.likes}</span>
            {t('common.likes', {
              count: counters.likes,
            })}
          </FooterItem>
          <FooterItem>
            <CommentExactIcon size={10} color="var(--textA-8)" />
            <span>{counters.mentions}</span>
            {t('common.mentions', {
              count: counters.mentions,
            })}
          </FooterItem>
        </Footer>
      </Frame>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  text-align: center;
  justify-content: center;
  gap: 5px;

  margin-block: 30px;

  scale: 1.2;
`
const Frame = styled.div`
  width: 199px;
  height: 142px;
  position: relative;

  display: grid;
  grid-template-rows: 60px auto 24px;

  &::before {
    content: '';

    position: absolute;
    inset: 0;

    background: ${DomUtil.cssUrl(BG_FRAME)};
    background-size: contain;
    background-repeat: no-repeat;
    background-position: 4px 0;

    pointer-events: none;
    z-index: 0;
  }

  [data-theme-appearance='light'] & {
    &::before {
      filter: invert(1);
    }
  }

  > * {
    position: relative;
    z-index: 1;
  }
`

const Error = styled.div`
  color: var(--error-9);
  text-align: center;

  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px; /* 120% */
`

const Header = styled.div`
  color: var(--textA-6);

  display: grid;
  grid-template-columns: 70px auto 70px;
  align-items: center;
  align-content: center;
  justify-items: center;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  gap: 2px;
  padding-inline: 5px;

  span {
    color: var(--textA-10);
    font-size: 11px;
    font-weight: 600;
  }
`

const Body = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  align-content: center;
  position: relative;

  backdrop-filter: blur(2px);
  margin-inline: 7px;
  margin-bottom: 5px;
`

const Footer = styled.div`
  color: var(--textA-8);

  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;

  font-size: 9px;
  font-weight: 400;
  line-height: 12px;
  gap: 8px;
  padding-inline: 5px;

  span {
    font-weight: 600;
  }
`

const HeaderItem = styled.div`
  font-size: 9px;
  font-weight: 400;
  line-height: 1;

  display: grid;
  grid-template-rows: auto auto;
  align-items: center;
  grid-auto-flow: column;
  justify-content: center;
  text-transform: capitalize;

  backdrop-filter: blur(2px);
  margin-inline: 6px;

  padding-top: 5px;
  width: 100%;

  html[lang='ru'] & {
    font-size: 8px;
  }
`

const FooterItem = styled.div`
  display: grid;
  align-items: center;
  gap: 3px;
  grid-auto-flow: column;
  justify-content: center;

  backdrop-filter: blur(2px);
`

const Username = styled.div`
  color: var(--text-12);

  font-size: 13px;
  font-weight: 600;
`

const Description = styled.div`
  color: var(--textA-8);

  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`

const WalletDescription = styled(WalletAddressTruncated)`
  color: var(--textA-8);

  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  justify-content: center;
`
